import { useReducer } from 'react';
import type React from 'react';

import type { SearchAndSelectOptionWithState, SelectOptionWithState } from 'bundles/onboarding-common/types';

export type State<T extends { id: string }> = {
  options: SearchAndSelectOptionWithState<T>[];
  singleSelectOption?: SelectOptionWithState<T>;
};

export type Action<T extends { id: string }> =
  | {
      type: 'ADD_ITEMS';
      payload: T[];
    }
  | {
      type: 'ADD_SEARCH_RESULT_ITEMS';
      payload: T[];
    }
  | {
      type: 'MARK_SEARCH_RESULTS_AS_NOT_VISIBLE';
    }
  | {
      type: 'SELECT_ITEM_BY_ID';
      payload: string;
    }
  | {
      type: 'DESELECT_ITEM_BY_ID';
      payload: string;
    }
  | {
      type: 'DESELECT_SINGLE_SELECT_OPTION';
    }
  | {
      type: 'SELECT_SINGLE_SELECT_OPTION';
    };

export const reducer = <T extends { id: string }>(state: State<T>, action: Action<T>) => {
  switch (action.type) {
    case 'ADD_ITEMS':
      return {
        ...state,
        options: action.payload.reduce((acc, curr) => {
          const ids = state.options.map(({ id }) => id);
          if (!ids.includes(curr.id)) {
            return [
              ...acc,
              { ...curr, isSearchResult: false, isSelected: false, isVisible: true, isPartOfSearch: false },
            ];
          }
          return acc;
        }, state.options),
      };

    case 'ADD_SEARCH_RESULT_ITEMS':
      return {
        ...state,
        options: action.payload.reduce((acc, curr) => {
          const ids = state.options.map(({ id }) => id);
          if (!ids.includes(curr.id)) {
            return [
              ...acc,
              { ...curr, isSearchResult: true, isSelected: false, isVisible: true, isPartOfSearch: true },
            ];
          }
          return acc.map((item) => (item.id === curr.id ? { ...item, isVisible: true, isPartOfSearch: true } : item));
        }, state.options),
      };

    case 'MARK_SEARCH_RESULTS_AS_NOT_VISIBLE':
      return {
        ...state,
        options: state.options.map((item) => ({ ...item, isVisible: !item.isSearchResult, isPartOfSearch: false })),
      };

    case 'SELECT_ITEM_BY_ID':
      return {
        options: state.options.map((item) => ({
          ...item,
          isSelected: item.id === action.payload ? true : item.isSelected,
        })),
        singleSelectOption: state.singleSelectOption
          ? {
              ...state.singleSelectOption,
              isSelected: false,
            }
          : undefined,
      };

    case 'DESELECT_ITEM_BY_ID':
      return {
        ...state,
        options: state.options.map((item) => ({
          ...item,
          isSelected: item.id === action.payload ? false : item.isSelected,
        })),
      };

    case 'DESELECT_SINGLE_SELECT_OPTION':
      if (!state.singleSelectOption) {
        return state;
      }

      return {
        ...state,
        singleSelectOption: {
          ...state.singleSelectOption,
          isSelected: false,
        },
      };

    case 'SELECT_SINGLE_SELECT_OPTION':
      if (!state.singleSelectOption) {
        return state;
      }

      return {
        options: state.options.map((item) => ({ ...item, isSelected: false })),
        singleSelectOption: {
          ...state.singleSelectOption,
          isSelected: true,
        },
      };

    default:
      return state;
  }
};

const useSearchAndSelectOptionState = <T extends { id: string }>(
  initialOptions?: State<T>['options'],
  initialSingleSelectOption?: State<T>['singleSelectOption']
) => {
  const initialState: State<T> = {
    options: initialOptions || [],
    singleSelectOption: initialSingleSelectOption,
  };

  const [state, dispatch] = useReducer<React.Reducer<State<T>, Action<T>>>(reducer, initialState);

  const items = state.options;
  const singleSelectOption = state.singleSelectOption;

  return { items, dispatch, singleSelectOption };
};

export default useSearchAndSelectOptionState;
